import React, { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { 
  QrCode, 
  Download, 
  Copy, 
  Share2, 
  Smartphone, 
  Link, 
  Mail,
  Phone,
  MapPin,
  Globe,
  Palette,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const QRCodeGenerator = () => {
  const [qrConfig, setQrConfig] = useState({
    text: 'https://referral-platform-8.preview.emergentagent.com/join',
    size: 256,
    fgColor: '#000000',
    bgColor: '#ffffff',
    level: 'M', // Error correction level
    includeMargin: true,
    type: 'url'
  });

  const [presetType, setPresetType] = useState('custom');
  const qrRef = useRef();

  const qrPresets = {
    website: {
      label: 'Website/Landing Page',
      icon: Globe,
      placeholder: 'https://yourwebsite.com',
      description: 'Link to your website or landing page'
    },
    referral: {
      label: 'Referral Link',
      icon: Share2,
      placeholder: 'https://referral-platform-8.preview.emergentagent.com/join?ref=YOUR_CODE',
      description: 'Your referral link for new signups'
    },
    email: {
      label: 'Email Address',
      icon: Mail,
      placeholder: 'mailto:your.email@example.com',
      description: 'Direct email contact'
    },
    phone: {
      label: 'Phone Number',
      icon: Phone,
      placeholder: 'tel:+1234567890',
      description: 'Direct phone call'
    },
    location: {
      label: 'Location/Address',
      icon: MapPin,
      placeholder: 'geo:40.7128,-74.0060',
      description: 'Geographic location'
    },
    text: {
      label: 'Plain Text',
      icon: QrCode,
      placeholder: 'Any text message',
      description: 'Simple text message'
    }
  };

  const handlePresetChange = (preset) => {
    setPresetType(preset);
    if (preset !== 'custom' && qrPresets[preset]) {
      setQrConfig(prev => ({
        ...prev,
        text: qrPresets[preset].placeholder,
        type: preset
      }));
    }
  };

  const downloadQR = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svg = qrRef.current.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    
    canvas.width = qrConfig.size;
    canvas.height = qrConfig.size;
    
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    img.onload = () => {
      ctx.drawImage(img, 0, 0, qrConfig.size, qrConfig.size);
      
      // Download the canvas as PNG
      const link = document.createElement('a');
      link.download = `qr-code-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      toast.success('QR Code downloaded successfully!');
    };
    
    img.src = url;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(qrConfig.text).then(() => {
      toast.success('Text copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy text');
    });
  };

  const shareQR = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My QR Code',
          text: qrConfig.text,
          url: qrConfig.text.startsWith('http') ? qrConfig.text : undefined
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      copyToClipboard();
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
          <QrCode className="h-8 w-8 mr-3 text-blue-600" />
          QR Code Generator
        </h1>
        <p className="text-gray-600">Create QR codes for your links, contact info, and marketing materials</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Configuration Panel */}
        <div className="space-y-6">
          {/* Preset Types */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">QR Code Type</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(qrPresets).map(([key, preset]) => {
                  const IconComponent = preset.icon;
                  return (
                    <Button
                      key={key}
                      variant={presetType === key ? 'default' : 'outline'}
                      className="flex flex-col h-auto p-3"
                      onClick={() => handlePresetChange(key)}
                    >
                      <IconComponent className="h-5 w-5 mb-1" />
                      <span className="text-xs">{preset.label}</span>
                    </Button>
                  );
                })}
              </div>

              {presetType !== 'custom' && qrPresets[presetType] && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">{qrPresets[presetType].description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Content Input */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="qr-text">Text/URL to encode</Label>
                <Textarea
                  id="qr-text"
                  value={qrConfig.text}
                  onChange={(e) => setQrConfig(prev => ({ ...prev, text: e.target.value }))}
                  placeholder="Enter URL, text, or contact information"
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              <Button
                variant="outline"
                onClick={copyToClipboard}
                className="w-full"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Text
              </Button>
            </CardContent>
          </Card>

          {/* Appearance Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Palette className="h-5 w-5 mr-2" />
                Appearance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="size">Size (pixels)</Label>
                  <Select 
                    value={qrConfig.size.toString()} 
                    onValueChange={(value) => setQrConfig(prev => ({ ...prev, size: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="128">128px (Small)</SelectItem>
                      <SelectItem value="256">256px (Medium)</SelectItem>
                      <SelectItem value="512">512px (Large)</SelectItem>
                      <SelectItem value="1024">1024px (Extra Large)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="level">Error Correction</Label>
                  <Select 
                    value={qrConfig.level} 
                    onValueChange={(value) => setQrConfig(prev => ({ ...prev, level: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L">Low (7%)</SelectItem>
                      <SelectItem value="M">Medium (15%)</SelectItem>
                      <SelectItem value="Q">Quartile (25%)</SelectItem>
                      <SelectItem value="H">High (30%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fg-color">Foreground Color</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <input
                      type="color"
                      id="fg-color"
                      value={qrConfig.fgColor}
                      onChange={(e) => setQrConfig(prev => ({ ...prev, fgColor: e.target.value }))}
                      className="w-12 h-10 rounded border border-gray-300"
                    />
                    <Input
                      value={qrConfig.fgColor}
                      onChange={(e) => setQrConfig(prev => ({ ...prev, fgColor: e.target.value }))}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="bg-color">Background Color</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <input
                      type="color"
                      id="bg-color"
                      value={qrConfig.bgColor}
                      onChange={(e) => setQrConfig(prev => ({ ...prev, bgColor: e.target.value }))}
                      className="w-12 h-10 rounded border border-gray-300"
                    />
                    <Input
                      value={qrConfig.bgColor}
                      onChange={(e) => setQrConfig(prev => ({ ...prev, bgColor: e.target.value }))}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="margin"
                  checked={qrConfig.includeMargin}
                  onChange={(e) => setQrConfig(prev => ({ ...prev, includeMargin: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="margin">Include margin</Label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview Panel */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Eye className="h-5 w-5 mr-2" />
                Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 rounded-lg p-8 flex items-center justify-center min-h-[400px]">
                <div 
                  ref={qrRef}
                  className="bg-white p-4 rounded-lg shadow-lg"
                  style={{ backgroundColor: qrConfig.bgColor }}
                >
                  {qrConfig.text ? (
                    <QRCodeSVG
                      value={qrConfig.text}
                      size={Math.min(qrConfig.size, 300)} // Limit preview size
                      fgColor={qrConfig.fgColor}
                      bgColor={qrConfig.bgColor}
                      level={qrConfig.level}
                      includeMargin={qrConfig.includeMargin}
                    />
                  ) : (
                    <div className="w-64 h-64 border-2 border-dashed border-gray-300 flex items-center justify-center rounded">
                      <span className="text-gray-500">Enter content to generate QR code</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                onClick={downloadQR} 
                disabled={!qrConfig.text}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PNG
              </Button>
              
              <Button 
                variant="outline" 
                onClick={shareQR}
                disabled={!qrConfig.text}
                className="w-full"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share QR Code
              </Button>

              <div className="pt-3 border-t">
                <h4 className="font-medium text-gray-900 mb-2">Usage Tips:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Test QR codes before printing</li>
                  <li>• Use high error correction for small prints</li>
                  <li>• Ensure good contrast between colors</li>
                  <li>• Keep URLs short for better scanning</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;